import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { serverDomain } from "../../../config";
import "../../../styles/WebmastersPage.css";

const WebmastersPage = () => {
  const [users, setUsers] = useState([]);
  const [percentageInputs, setPercentageInputs] = useState({});
  const [balanceInputs, setBalanceInputs] = useState({});
  const [balanceModes, setBalanceModes] = useState({}); // add | set

  const [emailSearch, setEmailSearch] = useState("");
  const [streamSearch, setStreamSearch] = useState("");

  const [loading, setLoading] = useState(false);

  // IP history
  const [ipOpenFor, setIpOpenFor] = useState(null);
  const [ipData, setIpData] = useState({});
  const [ipLoadingFor, setIpLoadingFor] = useState(null);

  const rowRefs = useRef({});

  const money = (v) =>
    (Number(v) || 0).toLocaleString("ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const pct = (v) => `${Number(v || 0)}%`;

  const prettyIp = (ip) => {
    if (!ip) return "—";
    if (ip === "::1") return "127.0.0.1 (локально)";
    if (ip.startsWith("::ffff:")) return ip.slice(7);
    return ip;
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${serverDomain}/api/admin/webmasters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data || []);
    } catch (error) {
      console.error("Ошибка при получении вебмастеров", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchIpHistory = async (userId) => {
    try {
      setIpLoadingFor(userId);
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${serverDomain}/api/admin/webmasters/${userId}/ip-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIpData((prev) => ({ ...prev, [userId]: data }));
    } catch (err) {
      console.error("Ошибка получения IP-истории:", err);
      alert("Не удалось загрузить IP-историю");
    } finally {
      setIpLoadingFor(null);
    }
  };

  const toggleIpRow = async (userId) => {
    if (ipOpenFor === userId) {
      setIpOpenFor(null);
      return;
    }
    setIpOpenFor(userId);
    if (!ipData[userId]) await fetchIpHistory(userId);
  };

  const handleInputChange = (userId, value) => {
    setPercentageInputs((prev) => ({ ...prev, [userId]: value }));
  };

  const handleSavePercentage = async (userId) => {
    const newPercentage = percentageInputs[userId];
    if (newPercentage === undefined || newPercentage === "") {
      alert("Введите значение процента");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${serverDomain}/api/admin/webmasters/${userId}/percentage`,
        { percentage: newPercentage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, percentage: newPercentage } : u)));
      setPercentageInputs((prev) => ({ ...prev, [userId]: "" }));
    } catch (error) {
      console.error("Ошибка при обновлении процента", error);
      alert("Не удалось обновить процент");
    }
  };

  const handleBalanceInputChange = (userId, value) => {
    setBalanceInputs((prev) => ({ ...prev, [userId]: value }));
  };

  const handleApplyBalance = async (userId, mode) => {
    const raw = balanceInputs[userId];
    if (raw === undefined || raw === "") return alert("Укажите сумму");
    const amount = Number(raw);
    if (!Number.isFinite(amount)) return alert("Некорректная сумма");

    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.put(
        `${serverDomain}/api/admin/webmasters/${userId}/balance-adjust`,
        { amount, mode }, // add | set
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, balance: data.balance } : u)));
      setBalanceInputs((prev) => ({ ...prev, [userId]: "" }));
      setBalanceModes((prev) => ({ ...prev, [userId]: "add" }));
    } catch (error) {
      console.error("Ошибка при изменении баланса:", error);
      alert("Не удалось изменить баланс");
    }
  };

  const handleSwitchAccount = async (userId) => {
    try {
      const currentToken = localStorage.getItem("token");
      localStorage.setItem("adminToken", currentToken);

      const response = await axios.post(
        `${serverDomain}/api/admin/switch-account`,
        { userId },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );

      localStorage.setItem("token", response.data.token);
      window.location.reload();
    } catch (error) {
      console.error("Ошибка входа в аккаунт вебмастера", error);
      alert("Не удалось войти в аккаунт");
    }
  };

  const handleToggleBlock = async (userId, isBlocked) => {
    try {
      const token = localStorage.getItem("token");
      const url = isBlocked
        ? `${serverDomain}/api/admin/webmasters/${userId}/unblock`
        : `${serverDomain}/api/admin/webmasters/${userId}/block`;

      const { data } = await axios.put(url, {}, { headers: { Authorization: `Bearer ${token}` } });

      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: data.role } : u)));
    } catch (error) {
      console.error("Ошибка блокировки/разблокировки:", error);
      alert("Не удалось выполнить операцию");
    }
  };

  const filteredUsers = useMemo(() => {
    const emailQ = emailSearch.trim().toLowerCase();
    const streamQ = streamSearch.trim().toLowerCase();
    if (!emailQ && !streamQ) return users;

    return users.filter((u) => {
      const emailOk = emailQ ? (u.email || "").toLowerCase().includes(emailQ) : true;
      const streamsArr = (u.streams || "")
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      const streamOk = streamQ ? streamsArr.includes(streamQ) : true;
      return emailOk && streamOk;
    });
  }, [users, emailSearch, streamSearch]);

  const onSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const q = emailSearch.trim().toLowerCase();
    if (!q) return;

    const exact = users.find((u) => (u.email || "").toLowerCase() === q);
    const idToScroll = exact?.id ?? filteredUsers[0]?.id;

    if (idToScroll && rowRefs.current[idToScroll]) {
      rowRefs.current[idToScroll].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const highlight = (text, q) => {
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + q.length);
    const after = text.slice(idx + q.length);
    return (
      <>
        {before}
        <mark className="wm-mark">{match}</mark>
        {after}
      </>
    );
  };

  return (
    <div className="fl-adminpage fl-webmasters">
      <div className="fl-adminpage__head">
        <h2 className="fl-h2">Вебмастера</h2>
        <div className="fl-adminpage__meta">
          Найдено: <b>{filteredUsers.length}</b>
        </div>
      </div>

      <div className="fl-toolbar fl-webmasters__toolbar">
        <div className="fl-webmasters__search">
          <input
            className="fl-input fl-input--wide"
            type="search"
            placeholder="Поиск по e-mail"
            value={emailSearch}
            onChange={(e) => setEmailSearch(e.target.value)}
            onKeyDown={onSearchKeyDown}
          />
          {emailSearch && (
            <button className="fl-iconbtn fl-iconbtn--ghost" type="button" onClick={() => setEmailSearch("")} title="Очистить">
              ×
            </button>
          )}
        </div>

        <div className="fl-webmasters__search">
          <input
            className="fl-input fl-input--wide"
            type="search"
            placeholder="Поиск по названию потока (точное совпадение)"
            value={streamSearch}
            onChange={(e) => setStreamSearch(e.target.value)}
          />
          {streamSearch && (
            <button className="fl-iconbtn fl-iconbtn--ghost" type="button" onClick={() => setStreamSearch("")} title="Очистить">
              ×
            </button>
          )}
        </div>

        <button className="fl-btn fl-btn--ghost" type="button" onClick={fetchUsers} disabled={loading}>
          {loading ? "Загрузка…" : "Обновить"}
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loader" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="fl-empty">Ничего не найдено</div>
      ) : (
        <div className="fl-table-wrap">
          <table className="fl-table fl-webmasters__table">
            <thead>
              <tr>
                <th className="col-id">ID</th>
                <th className="col-name">Имя</th>
                <th className="col-email">Email</th>
                <th className="col-pct fl-num">Процент</th>
                <th className="col-bal fl-num">Баланс</th>
                <th className="col-actions">Действия</th>
              </tr>
            </thead>

            <tbody>
              {filteredUsers.map((user) => {
                const isBlocked = user.role === "blocked";
                const mode = balanceModes[user.id] || "add";

                return (
                  <React.Fragment key={user.id}>
                    <tr ref={(el) => (rowRefs.current[user.id] = el)} className={isBlocked ? "wm-rowBlocked" : ""}>
                      <td className="col-id">{user.id}</td>
                      <td className="col-name">{user.name}</td>

                      <td className="col-email">
                        <div className="wm-emailLine">{highlight(user.email || "", emailSearch)}</div>

                        <div className="wm-emailActions">
                          <button className="fl-btn fl-btn--mini" type="button" onClick={() => handleSwitchAccount(user.id)}>
                            Войти
                          </button>

                          <button
                            className={`fl-btn fl-btn--mini ${isBlocked ? "fl-btn--ok" : "fl-btn--danger"}`}
                            type="button"
                            onClick={() => handleToggleBlock(user.id, isBlocked)}
                          >
                            {isBlocked ? "Разблокировать" : "Заблокировать"}
                          </button>

                          <span className={`wm-role ${isBlocked ? "wm-role--blocked" : "wm-role--active"}`}>
                            {isBlocked ? "blocked" : "active"}
                          </span>
                        </div>
                      </td>

                      <td className="col-pct fl-num">{pct(user.percentage)}</td>
                      <td className="col-bal fl-num">{money(user.balance)}</td>

                      <td className="col-actions">
                        <div className="wm-controls">
                          {/* % */}
                          <div className="wm-ctrlRow">
                            <div className="wm-ctrlLabel">% ставка</div>
                            <input
                              className="fl-input fl-input--mini"
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              inputMode="numeric"
                              placeholder="Новый %"
                              value={percentageInputs[user.id] || ""}
                              onChange={(e) => handleInputChange(user.id, e.target.value)}
                            />
                            <button className="fl-btn fl-btn--mini" type="button" onClick={() => handleSavePercentage(user.id)}>
                              Изменить
                            </button>
                          </div>

                          {/* Баланс */}
                          <div className="wm-ctrlRow">
                            <div className="wm-ctrlLabel">Баланс</div>
                            <input
                              className="fl-input fl-input--mini"
                              type="number"
                              step="0.01"
                              inputMode="decimal"
                              placeholder="Сумма"
                              value={balanceInputs[user.id] || ""}
                              onChange={(e) => handleBalanceInputChange(user.id, e.target.value)}
                            />
                            <select
                              className="fl-select fl-select--mini"
                              value={mode}
                              onChange={(e) => setBalanceModes((prev) => ({ ...prev, [user.id]: e.target.value }))}
                            >
                              <option value="add">Прибавить/Списать</option>
                              <option value="set">Установить</option>
                            </select>
                            <button className="fl-btn fl-btn--mini" type="button" onClick={() => handleApplyBalance(user.id, mode)}>
                              OK
                            </button>
                          </div>

                          {/* IP */}
                          <div className="wm-ctrlRow">
                            <div className="wm-ctrlLabel">IP</div>
                            <button className="fl-btn fl-btn--mini fl-btn--ghost" type="button" onClick={() => toggleIpRow(user.id)}>
                              {ipLoadingFor === user.id ? "Загрузка…" : ipOpenFor === user.id ? "Скрыть" : "Показать"}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>

                    {ipOpenFor === user.id && (
                      <tr className="wm-ipRow">
                        <td colSpan={6}>
                          <div className="wm-ipBox">
                            {ipLoadingFor === user.id ? (
                              <div className="wm-ipLoading">Загрузка IP-истории…</div>
                            ) : (
                              <>
                                <div className="wm-ipReg">
                                  <b>IP регистрации:</b> {prettyIp(ipData[user.id]?.registration_ip)}
                                  {ipData[user.id]?.registration_at ? (
                                    <span className="wm-ipRegAt">
                                      ({new Date(ipData[user.id].registration_at).toLocaleString("ru-RU")})
                                    </span>
                                  ) : null}
                                </div>

                                <div className="fl-table-wrap">
                                  <table className="fl-table wm-ipTable">
                                    <thead>
                                      <tr>
                                        <th>IP</th>
                                        <th>Источник</th>
                                        <th>Дата/время</th>
                                        <th>User-Agent</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(ipData[user.id]?.items || []).map((row, i) => (
                                        <tr key={i}>
                                          <td>{prettyIp(row.ip)}</td>
                                          <td>{row.action === "register" ? "Регистрация" : row.action === "login" ? "Вход" : row.action}</td>
                                          <td>{new Date(row.at).toLocaleString("ru-RU")}</td>
                                          <td className="wm-ua" title={row.user_agent || ""}>
                                            {row.user_agent || "—"}
                                          </td>
                                        </tr>
                                      ))}
                                      {(!ipData[user.id]?.items || ipData[user.id].items.length === 0) && (
                                        <tr>
                                          <td colSpan={4} className="wm-ipEmpty">
                                            Нет данных
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WebmastersPage;